import { HttpApiService } from "@/shared/services/httpApi.service";

/**
 * Outbound webhooks of the signed-in organization — `coloca-nest`
 * `src/coloca/organization-webhooks/organization-webhooks.internal.controller.ts`, mounted under
 * `/internal/organization-webhooks`. Every handler is org-scoped from the JWT: an organization member never
 * sends `organization_id`, and this service is only rendered for members (the section is hidden from staff).
 */

export type OrganizationWebhookDeliveryStatus = "pending" | "delivered" | "failed" | "exhausted";
/** Domain group of an event — what the receiver routes on first. Mirrors `OrganizationWebhookEventType`. */
export type OrganizationWebhookEventType = "payments" | "identities" | "cards" | "wallets" | "test";
/**
 * The specific action inside a group, named `<resource>_<past participle>`. Mirrors the backend
 * `OrganizationWebhookEvent` enum (`organization-webhook-event.enums.ts`).
 */
export type OrganizationWebhookEvent =
  // payments
  | "payin_created"
  | "payin_pending"
  | "payin_approved"
  | "payin_confirmed"
  | "payin_settled"
  | "payin_rejected"
  | "payin_reversed"
  | "payout_created"
  | "payout_processing"
  | "payout_paid"
  | "payout_failed"
  | "payout_cancelled"
  | "payout_reversed"
  | "swap_completed"
  // identities
  | "identity_created"
  | "kyc_approved"
  | "kyc_rejected"
  // cards
  | "cardholder_created"
  | "card_created"
  | "card_status_changed"
  | "card_transaction_authorized"
  | "card_transaction_declined"
  | "card_transaction_refunded"
  | "card_transaction_reversed"
  | "card_topped_up"
  | "card_deposit_received"
  | "card_withdrawn"
  | "card_wallet_recharged"
  // wallets
  | "wallet_created"
  // test
  | "ping";
/** Stamped by the backend when it stops an endpoint itself — one value per rule. */
export type OrganizationWebhookDisabledReason =
  | "auto_disabled_failure_streak"
  | "auto_disabled_prolonged_failure"
  /** The re-verification after a rotate or a re-point did not get the secret echoed back. */
  | "disabled_handshake_failed";

/**
 * The header every delivery carries the secret in (`ORGANIZATION_WEBHOOK_SECRET_HEADER` backend-side). Only a
 * fallback for the screens that render the contract before an endpoint exists — a live config states its
 * own.
 */
export const DEFAULT_SECRET_HEADER = "x-secret";

export const ORGANIZATION_WEBHOOK_DELIVERY_STATUSES: readonly OrganizationWebhookDeliveryStatus[] = [
  "pending",
  "delivered",
  "failed",
  "exhausted",
];

/** `GET /config` — the secret is always masked here; the plaintext only travels on create and rotate. */
export interface OrganizationWebhookConfig {
  id: string;
  url: string;
  /** `whsec_a1b2c3d4****` — enough to recognise the credential, never enough to use it. */
  secret_masked: string;
  /** Name of the header every delivery carries the secret in (`x-secret`). */
  secret_header: string;
  is_active: boolean;
  disabled_at: string | null;
  disabled_reason: OrganizationWebhookDisabledReason | string | null;
  consecutive_failures: number;
  last_success_at: string | null;
  last_failure_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * `PUT /config` and `POST /config/rotate-secret`. `secret` is present only when the backend just set it — on
 * create, on rotate, and when the caller supplied their own.
 */
export interface OrganizationWebhookSecretResponse extends OrganizationWebhookConfig {
  secret?: string;
  /**
   * The verdict of the re-verification the backend runs whenever the change invalidated the existing proof —
   * a rotate, a new URL, a new secret. Absent when nothing was re-verified (a create, a pause, a plain
   * re-save). Anything but `verified` leaves the endpoint paused with `disabled_handshake_failed`.
   */
  handshake?: OrganizationWebhookHandshakeResult;
}

export interface UpsertOrganizationWebhookRequest {
  url: string;
  is_active?: boolean;
  /**
   * The value Coloca will send in the `x-secret` header. Send it to match a secret your receiver already has;
   * omit it and the backend generates one on create, or keeps the current one on update.
   */
  secret?: string;
}

/** Bounds the backend validates `secret` against (`@Length(16, 255)` + printable ASCII, no spaces). */
export const ORGANIZATION_WEBHOOK_SECRET_MIN_LENGTH = 16;
export const ORGANIZATION_WEBHOOK_SECRET_MAX_LENGTH = 255;
export const ORGANIZATION_WEBHOOK_SECRET_PATTERN = /^[\x21-\x7e]+$/;
/** Prefix the backend puts on the secrets it generates (`generateOrganizationWebhookSecret`). */
export const ORGANIZATION_WEBHOOK_SECRET_PREFIX = "whsec_";
/** Random bytes behind a generated secret, matching the backend's `randomBytes(32)`. */
export const ORGANIZATION_WEBHOOK_SECRET_BYTES = 32;

/** `POST /config/reveal-secret` — the live credential, plus the header it belongs in. */
export interface OrganizationWebhookRevealedSecret {
  secret: string;
  secret_header: string;
}

/**
 * What the configuration handshake concluded. The receiver has to echo the secret back — a 2xx alone proves
 * nothing about who is listening — so each value names a different fix for the integrator.
 */
export type OrganizationWebhookHandshakeResult =
  | "verified"
  | "no_response"
  | "http_error"
  | "invalid_response"
  | "secret_missing"
  | "secret_mismatch";

/** `POST /test` — synchronous; the result of the handshake against the receiver. */
export interface OrganizationWebhookTestResult {
  /** True only when the receiver echoed the configured secret. */
  success: boolean;
  handshake: OrganizationWebhookHandshakeResult;
  status_code: number | null;
  response_time_ms: number;
  error_message: string | null;
  /** The delivery row the test was logged as — it shows up in the report. */
  delivery_id: string;
}

/** One row of `organization_webhook_deliveries`. Append-only on the backend. */
export interface OrganizationWebhookDelivery {
  id: string;
  organization_id: string;
  organization_webhook_id: string | null;
  type: OrganizationWebhookEventType;
  event: OrganizationWebhookEvent;
  /**
   * The id of the resource the event is about — a payin, payout, swap, identity, card, card transaction,
   * wallet… (`data.id` of the envelope); null on a test ping.
   */
  event_id: string | null;
  /** Snapshot of the endpoint URL at enqueue time. */
  url: string;
  /** The envelope exactly as it was POSTed. */
  payload: OrganizationWebhookEnvelope;
  status: OrganizationWebhookDeliveryStatus;
  attempt_count: number;
  last_status_code: number | null;
  /** Truncated by the backend to 2 KB. */
  response_body: string | null;
  response_time_ms: number | null;
  last_error: string | null;
  delivered_at: string | null;
  created_at: string;
}

/** The v2 envelope. `id` is the delivery id the receiver deduplicates on. */
export interface OrganizationWebhookEnvelope {
  id: string;
  type: OrganizationWebhookEventType;
  event: OrganizationWebhookEvent;
  created_at: string;
  /** The resource exactly as its public `GET /:id` returns it, frozen when the event was dispatched. */
  data: Record<string, unknown>;
}

export interface OrganizationWebhookEventCatalogEntry {
  type: OrganizationWebhookEventType;
  events: OrganizationWebhookEvent[];
}

/**
 * Static copy of the backend `ORGANIZATION_WEBHOOK_EVENT_CATALOG`, for the screens that document the contract
 * before an endpoint exists (the integration guide). The report's filters still read `GET /event-types`,
 * which stays the source of truth for what can be filtered on.
 */
export const ORGANIZATION_WEBHOOK_EVENT_CATALOG: readonly OrganizationWebhookEventCatalogEntry[] = [
  {
    type: "payments",
    events: [
      "payin_created",
      "payin_pending",
      "payin_approved",
      "payin_confirmed",
      "payin_settled",
      "payin_rejected",
      "payin_reversed",
      "payout_created",
      "payout_processing",
      "payout_paid",
      "payout_failed",
      "payout_cancelled",
      "payout_reversed",
      "swap_completed",
    ],
  },
  { type: "identities", events: ["identity_created", "kyc_approved", "kyc_rejected"] },
  {
    type: "cards",
    events: [
      "cardholder_created",
      "card_created",
      "card_status_changed",
      "card_transaction_authorized",
      "card_transaction_declined",
      "card_transaction_refunded",
      "card_transaction_reversed",
      "card_topped_up",
      "card_deposit_received",
      "card_withdrawn",
      "card_wallet_recharged",
    ],
  },
  { type: "wallets", events: ["wallet_created"] },
  { type: "test", events: ["ping"] },
];

/** Shape of every paginated coloca-nest list (`paginate()` in `src/common/dto`). */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}

/**
 * Server-side filters of the report, already serialised the way the query DTO wants them: `status_code` as a
 * number, `from` / `to` as full ISO-8601 instants (`@IsDateString`, not a day).
 */
export interface OrganizationWebhookDeliveriesQuery {
  skip: number;
  limit: number;
  status?: OrganizationWebhookDeliveryStatus;
  type?: OrganizationWebhookEventType;
  event?: OrganizationWebhookEvent;
  status_code?: number;
  event_id?: string;
  from?: string;
  to?: string;
}

/** `last_error` marker the processor writes when it drops a queued job because the endpoint was paused. */
export const ORGANIZATION_WEBHOOK_ENDPOINT_DISABLED_ERROR = "endpoint_disabled";

/** Backend `PaginationDto.Max(200)` — requesting more is a 400, not a clamp. */
export const ORGANIZATION_WEBHOOK_DELIVERIES_MAX_LIMIT = 200;

const BASE = "/internal/organization-webhooks";

/**
 * Query string from a flat param bag, skipping `undefined` and `""` so an unset filter is absent rather than
 * sent empty (an empty `status` would fail `@IsEnum`).
 */
function toQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export const organizationWebhooksService = {
  /**
   * The organization's endpoint, or `null` when none is configured yet. The backend answers that case with a
   * 404 (`not_configured`), which for this screen is the empty state, not a failure — so it is absorbed here
   * and every other error still propagates.
   */
  async getConfig(): Promise<OrganizationWebhookConfig | null> {
    try {
      const response = await HttpApiService.get<OrganizationWebhookConfig>(`${BASE}/config`);
      return response.data;
    } catch (error: unknown) {
      if (HttpApiService.isNotFoundError(error)) return null;
      throw error;
    }
  },

  /** Create or update the endpoint. `secret` comes back only when this call created it. */
  async upsertConfig(body: UpsertOrganizationWebhookRequest): Promise<OrganizationWebhookSecretResponse> {
    const response = await HttpApiService.put<OrganizationWebhookSecretResponse>(`${BASE}/config`, body);
    return response.data;
  },

  /**
   * Reads the live secret back in plaintext. The only endpoint that does, gated on
   * `organization_webhooks:write` and audited on the backend — so it is called on an explicit gesture, never
   * to populate a screen.
   */
  async revealSecret(): Promise<OrganizationWebhookRevealedSecret> {
    const response = await HttpApiService.post<OrganizationWebhookRevealedSecret>(
      `${BASE}/config/reveal-secret`,
      {},
    );
    return response.data;
  },

  /** New secret, returned in full once. Deliveries switch to it immediately. */
  async rotateSecret(): Promise<OrganizationWebhookSecretResponse> {
    const response = await HttpApiService.post<OrganizationWebhookSecretResponse>(
      `${BASE}/config/rotate-secret`,
      {},
    );
    return response.data;
  },

  /**
   * Deletes the endpoint row outright (204) — a documented exception to the soft-delete rule, so
   * re-registering does not accumulate rows behind the partial unique index. The delivery history survives
   * it: those rows carry no foreign key back here.
   */
  async deleteConfig(): Promise<void> {
    await HttpApiService.delete(`${BASE}/config`);
  },

  /**
   * Pings the receiver synchronously with a `type: "test"` / `event: "ping"` event and reports what it
   * answered.
   */
  async testConnection(): Promise<OrganizationWebhookTestResult> {
    const response = await HttpApiService.post<OrganizationWebhookTestResult>(`${BASE}/test`, {});
    return response.data;
  },

  /** Static `type → event[]` catalog, so the filter dropdowns never hardcode the event list. */
  async getEventTypes(): Promise<OrganizationWebhookEventCatalogEntry[]> {
    const response = await HttpApiService.get<OrganizationWebhookEventCatalogEntry[]>(`${BASE}/event-types`);
    return response.data;
  },

  async getDeliveries(
    query: OrganizationWebhookDeliveriesQuery,
  ): Promise<PaginatedResponse<OrganizationWebhookDelivery>> {
    const response = await HttpApiService.get<PaginatedResponse<OrganizationWebhookDelivery>>(
      `${BASE}/deliveries${toQuery({
        skip: query.skip,
        limit: Math.min(query.limit, ORGANIZATION_WEBHOOK_DELIVERIES_MAX_LIMIT),
        status: query.status,
        type: query.type,
        event: query.event,
        status_code: query.status_code,
        event_id: query.event_id,
        from: query.from,
        to: query.to,
      })}`,
    );
    return response.data;
  },

  async getDelivery(id: string): Promise<OrganizationWebhookDelivery> {
    const response = await HttpApiService.get<OrganizationWebhookDelivery>(`${BASE}/deliveries/${id}`);
    return response.data;
  },

  /**
   * Re-queues a `failed` / `exhausted` delivery against the current endpoint URL (202). A `pending` or
   * `delivered` row answers 400 `delivery_not_retryable`.
   */
  async retryDelivery(id: string): Promise<OrganizationWebhookDelivery> {
    const response = await HttpApiService.post<OrganizationWebhookDelivery>(
      `${BASE}/deliveries/${id}/retry`,
      {},
    );
    return response.data;
  },
};
